import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { getMenusUserIDWise } from '../../features/menu/menuSlice';

const ROUTE_PERMISSION_ALIASES = {
  '/addrecipe': '/recipe',
  '/addbranch': '/branches',
  '/userbranchassignments': '/cashcounters',
  '/addpurchaserequest': '/purchaserequest',
  '/addpurchaseorder': '/purchaseorder',
  '/addgrn': '/grn',
  '/addpurchaseinvoice': '/purchaseinvoice',
  '/restaurantpos': '/pos',
  '/bakerypos': '/pos',
  '/bakeryforwardinvoice': '/pos',
  '/bakerycashcounter': '/pos',
  '/dinetakeawaydashboard': '/deliverydashboard',
  '/dashboard': '/pos',
};

const normalizePath = (value) => {
  const normalized = String(value || '').trim().split('?')[0].split('#')[0].toLowerCase();
  if (!normalized || normalized === '/') return '/';
  return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
};

const getAllowedPaths = (navbarMenu) => new Set(
  (Array.isArray(navbarMenu) ? navbarMenu : []).flatMap((headMenu) =>
    (Array.isArray(headMenu?.ArrSubMenu) ? headMenu.ArrSubMenu : [])
      .map((subMenu) => normalizePath(subMenu?.menuURL))
      .filter((path) => path && path !== '/')
  )
);

export default function ProtectedRoute({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { navbarMenu, permissionStatus, permissionUserID } = useSelector((state) => state.menus);
  const userID = user?.userID;

  React.useEffect(() => {
    if (
      userID &&
      (permissionUserID !== userID || permissionStatus === 'idle')
    ) {
      dispatch(getMenusUserIDWise({ userID }));
    }
  }, [dispatch, permissionStatus, permissionUserID, userID]);

  if (!user) {
    return <Navigate to="/Login" replace />;
  }

  if (permissionUserID !== userID || permissionStatus === 'idle' || permissionStatus === 'loading') {
    return (
      <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={34} />
          <Typography sx={{ mt: 2 }}>Checking screen permission...</Typography>
        </Box>
      </Box>
    );
  }

  const allowedPaths = getAllowedPaths(navbarMenu);
  const requestedPath = normalizePath(location.pathname);
  const requiredPath = ROUTE_PERMISSION_ALIASES[requestedPath] || requestedPath;
  const isAllowed = permissionStatus === 'succeeded' && allowedPaths.has(requiredPath);

  if (!isAllowed) {
    const firstAllowedPath = Array.from(allowedPaths)[0];

    return (
      <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Box sx={{ width: 'min(520px, 100%)' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            You do not have permission to open this screen.
          </Alert>
          <Typography variant="h4" sx={{ mb: 1 }}>Access Denied</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Please contact the administrator if you need access.
          </Typography>
          {firstAllowedPath && (
            <Button variant="contained" onClick={() => navigate(firstAllowedPath, { replace: true })}>
              Go to an allowed screen
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return children;
}
